import {
  Box,
  Button,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  makeStyles,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core'
import { LoadingContainer } from 'components/Common/LoadingContainer'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'
import { saveBomItemTargetPrice } from 'features/BillOfMaterials/store/asyncActions/saveBomItemTargetPrice'
import { ProjectTextField } from 'features/BillOfMaterials/store/components/ProjectTextField'
import { bomItemFinancialSelector } from 'features/BillOfMaterials/store/selectors/bomItemFinancialSelector'
import _ from 'lodash'
import { MoneyString } from 'model/Money'
import { BoMItemType } from 'model/Project/BoMItemRow'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from 'store/configureStore'
import {
  MoneyDto,
  PriceRowDto,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { RootAppState } from 'store/configureStore'
import { RowThumbnailButton } from '../../../../../features/BillOfMaterials/components/BomItemThumbnailButton'
import { FinancialDetailsGraph } from '../../../../../features/BomItemDetailsDrawer/Graph/FinancialDetailsGraph'
import { useFinancialDetails } from '../../../../../features/BomItemDetailsDrawer/useBomItemDetails'
import { FinancialDetailsTable } from './FinancialDetailsTable'

export type FinancialDetailsProps = {
  open?: boolean
  onClose?: () => void
  bomItemId: string
  disableChanges?: boolean
  bomItemType: BoMItemType
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    targetPriceDetailsRoot: {
      display: 'flex',
      // padding: theme.spacing(2),
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
    dialogTitle: {
      display: 'flex',
      alignItems: 'center',
      paddingLeft: theme.spacing(2),
      gap: theme.spacing(2),
      minHeight: '80px',
    },
    dialogContent: {
      minHeight: '60vh',
      padding: theme.spacing(0, 2),
    },
    actions: {
      display: 'flex',
      alignItems: 'flex-end',
      padding: theme.spacing(2),
      // marginTop: theme.spacing(1),
      // justifyContent: 'space-around',
      // boxShadow: theme.shadows['2'],
      '& button': {
        marginLeft: theme.spacing(1),
      },
    },

    actionButtons: {
      textAlign: 'right',
      flex: '0 0 auto',
    },
    detailsRoot: {
      display: 'flex',
      // flexWrap: 'wrap',
      // width: '80vw',
      // height: '40vh',
      // overflow: 'auto',
      [theme.breakpoints.down(theme.breakpoints.values.lg)]: {
        flexDirection: 'column',
        width: '100%',
      },
    },
    graphContainer: {
      minHeight: '40vh',
      alignItems: 'center',
      // padding: theme.spacing(2),
      width: '40%',
      [theme.breakpoints.down(theme.breakpoints.values.lg)]: {
        width: '100%',
        height: '20vh',
        minHeight: 0,
      },
      // flexGrow: 1,
    },
    financialDetailsContainer: {
      width: '60%',
      // height: '35vh',
      overflowY: 'auto',
      paddingTop: theme.spacing(2.2),
      // flex: '1 1 auto',
      [theme.breakpoints.down(theme.breakpoints.values.lg)]: {
        maxHeight: '100%',
        width: '100%',
        marginTop: '.5em',
      },
    },
    targetPriceContainer: {
      display: 'flex',
      alignItems: 'space-around',
      justifyContent: 'center',
      width: '100%',
      [theme.breakpoints.down(theme.breakpoints.values.xl)]: {
        maxHeight: '50%',
      },
      // padding: theme.spacing(0, 2),
    },
  })
)

export const FinancialDetailsDialog = (props: FinancialDetailsProps) => {
  const cn = useStyles()
  const { t } = useTranslation()

  const { rowName, thumbnail3d } = useAppSelector((state: RootAppState) => {
    if (props.bomItemType === BoMItemType.project) {
      return {
        rowName: state.project.activeProject.projectReference,
        thumbnail3d: null,
        type: 'project',
      }
    }
    const bomItem =
      state.project.flatView[props.bomItemId] ||
      state.project.treeView[props.bomItemId]

    if (!bomItem) return null

    switch (bomItem?.type) {
      case 'AssemblyHeader':
        return {
          thumbnail3d: bomItem.assembly.thumbnail3d,
          rowName: bomItem.assembly.name,
          type: bomItem.type,
        }
      case 'Assembly':
        return {
          thumbnail3d: bomItem.thumbnail3d,
          rowName: bomItem.name,
          type: bomItem.type,
        }
      case 'Row':
        return {
          thumbnail3d: bomItem.thumbnail3d,
          rowName: bomItem.name,
          type: bomItem.type,
          isPurchasingPart: bomItem.isPurchasingBomItem,
        }
      default:
        return null
    }
  }, _.isEqual)

  const {
    loading,
    summarizedPriceDetails,
    priceDetails: rowDetails,
    getItemDetails,
    setLoading,
    financialViewUnit,
    handleChangeFinancialView,
    totalProjectQuantity,
    bomItemFinancial,
  } = useFinancialDetails({
    ...props,
  })

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleOnEditFinished = (newData: PriceRowDto) => {
    setLoading({ ['get-item-details']: true })
    setTimeout(() => {
      getItemDetails()
      // setUpdate((s) => ++s)
    }, 2000)
  }

  return (
    <Dialog
      open={props.open}
      fullWidth
      maxWidth="xl"
      onClose={props.onClose}
      // onEscapeKeyDown={() => props.onClose && props.onClose()}
      // disableEscapeKeyDown={false}
      classes={{ paper: cn.targetPriceDetailsRoot }}
    // onKeyDown={(e) => hasChanges && e.key === Key.Enter && handleSave()}
    >
      <div className={cn.dialogTitle}>
        {thumbnail3d && (
          <RowThumbnailButton
            size="large"
            bomItemType={props.bomItemType}
            rowId={props.bomItemId}
            disableClick
          // showTooltip
          />
        )}

        <div>
          <Typography variant="h6">{rowName}</Typography>
          <Box display="flex" alignItems="baseline">
            <LocalizedTypography
              translationKey="project:financial-details-title"
              variant="body2"
              color="textSecondary"
            >
              Financial Details
            </LocalizedTypography>
          </Box>
        </div>
      </div>
      <DialogContent className={cn.dialogContent}>
        <LoadingContainer
          loading={
            !rowDetails &&
            !summarizedPriceDetails &&
            loading['get-item-details']
          }
          noMessage
        >
          {rowDetails && summarizedPriceDetails && (
            <div className={cn.detailsRoot}>
              <div className={cn.graphContainer}>
                <FinancialDetailsGraph
                  bomItemId={props.bomItemId}
                  priceDetails={summarizedPriceDetails}
                  open={props.open}
                  targetPrice={
                    financialViewUnit === 'perUnit' &&
                    bomItemFinancial?.targetSalesPricePerItem
                  }
                  bomItemType={props.bomItemType}
                />
              </div>
              <div className={cn.financialDetailsContainer}>
                <FinancialDetailsTable
                  boMItemType={BoMItemType.row}
                  priceDetails={rowDetails}
                  rowId={props.bomItemId}
                  rowIssues={[]}
                  onEditFinished={handleOnEditFinished}
                  loading={loading['get-item-details']}
                  onChangeFinancialUnit={handleChangeFinancialView}
                  financialUnit={financialViewUnit}
                  totalProjectQuantity={totalProjectQuantity}
                />
              </div>
            </div>
          )}
        </LoadingContainer>
      </DialogContent>
      <DialogActions className={cn.actions}>
        {/* <div className={cn.actions}> */}
        <div className={cn.targetPriceContainer}>
          <TextField
            label={t('project:price-per-batch')}
            value={MoneyString(bomItemFinancial?.salesPricePerItem)}
            InputProps={{
              disableUnderline: true,
            }}
          />
          <div>
            {
              /* <form id={'target-price'}>
              <MoneyInput
                value={bomItemFinancial?.targetSalesPricePerItem}
                disabled={props.disableChanges}
                onChange={(e) =>
                  handleChangeTargetPrice(e.currentTarget.value as MoneyDto)
                }
                label={t('common:target-price', 'target price')}
                autoFocus
                endAdornment={
                  !props.disableChanges && (
                    <Tooltip title={'set current price as target price'}>
                      <IconButton
                        size="small"
                        disabled={props.disableChanges}
                        onClick={() =>
                          handleChangeTargetPrice(
                            bomItemFinancial?.salesPricePerItem
                          )
                        }
                        style={{ marginLeft: '1em' }}
                      >
                        <FileCopyOutlined />
                      </IconButton>
                    </Tooltip>
                  )
                }
              /> */
              <ProjectTextField
                valueSelector={bomItemFinancialSelector(
                  props.bomItemId,
                  'targetSalesPricePerItem'
                )}
                textFieldProps={{
                  label: t('common:target-price', 'target price'),
                }}
                actionTypePrefix={saveBomItemTargetPrice.typePrefix.concat(
                  '/',
                  props.bomItemId
                )}
                saveAction={(value: MoneyDto) =>
                  saveBomItemTargetPrice({
                    bomItemId: props.bomItemId,
                    targetPrice: value,
                  })
                }
                money
              />
            }
            {/* {bomItemFinancial?.targetSalesPricePerItem.value > 0 && (
              <LocalizedButton
                translationKey={'project:target-price-remove'}
                size="small"
                style={{
                  display: 'block',
                  width: '100%',
                  // flex: '0 0 auto',
                  // alignSelf: 'flex-end',
                  // marginRight: '1em',
                }}
                color="secondary"
                variant="text"
                onClick={handleRemoveTargetPrice}
              />
            )} */}
            {/* </form> */}
          </div>
        </div>
        <div className={cn.actionButtons}>
          <Button onClick={props.onClose}>{t('common:close')}</Button>
        </div>
      </DialogActions>
    </Dialog>
  )
}
